if (!customElements.get('northlight-page')) {
  class NorthlightPage extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.addEventListener('click', event => this.handleClick(event), opts);
      this.addEventListener('submit', event => { if (event.target.matches('.nl-form')) this.addToCart(event); }, opts);
      this.addEventListener('change', event => { if (event.target.matches('[name="quantity"]')) this.clampQuantity(); }, opts);
      this.addEventListener('keydown', event => this.handleKeys(event), opts);
      this.addEventListener('pointerdown', event => {
        if (event.isPrimary && event.target.closest('[data-nl-gallery-stage]')) this.gesture = { x: event.clientX, y: event.clientY, id: event.pointerId };
      }, opts);
      this.addEventListener('pointerup', event => {
        if (!this.gesture || event.pointerId !== this.gesture.id) return;
        const x = event.clientX - this.gesture.x, y = event.clientY - this.gesture.y;
        this.gesture = null;
        if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y) * 1.3) { this.showSlide(this.slideIndex + (x < 0 ? 1 : -1)); this.noZoomUntil = performance.now() + 350; }
      }, opts);
      this.addEventListener('pointercancel', () => { this.gesture = null; }, opts);
      this.addEventListener('close', event => { if (event.target.matches('[data-nl-lightbox]')) this.zoomOpener?.focus({ preventScroll: true }); }, { ...opts, capture: true });
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(name => document.addEventListener(name, () => this.refresh(), opts));
      document.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest('[data-nl-slide]') || event.target.querySelector('[data-nl-slide]');
        if (slide) this.showSlide(Number(slide.dataset.nlSlide));
        const tab = event.target.closest('[data-nl-tab]') || event.target.querySelector('[data-nl-tab]');
        if (tab) this.activateTab(tab.closest('[data-nl-tabs]'), tab.dataset.nlTab);
        const disclosure = event.target.closest('details') || event.target.querySelector('details');
        if (disclosure) disclosure.open = true;
      }, opts);
      window.addEventListener('scroll', () => this.scheduleSticky(), { ...opts, passive: true });
      window.addEventListener('resize', () => this.scheduleSticky(), { ...opts, passive: true });
      window.addEventListener('popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        this.selectVariant(id || this.variants?.[0]?.id, true, false);
      }, opts);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCartCount());
      }
      this.refresh();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.unsubscribe?.(); this.purchaseObserver?.disconnect(); this.popupObserver?.disconnect(); this.cartObserver?.disconnect();
      clearTimeout(this.countTimer); this.countRequestVersion = (this.countRequestVersion || 0) + 1;
      cancelAnimationFrame(this.stickyFrame);
    }
    refresh() {
      this.form = this.querySelector('.nl-form'); this.quantity = this.form?.querySelector('[name="quantity"]');
      this.slides = [...this.querySelectorAll('[data-nl-slide]')];
      try { this.variants = JSON.parse(this.querySelector('[data-nl-variants]')?.textContent || '[]'); } catch { this.variants = []; }
      try { this.words = JSON.parse(this.querySelector('[data-nl-words]')?.textContent || '{}'); } catch { this.words = {}; }
      const id = this.currentVariant?.id || this.form?.querySelector('[name="id"]')?.value;
      if (this.variants.length) this.selectVariant(id, false, false);
      this.showSlide(this.slideIndex || 0);
      this.querySelectorAll('[data-nl-tabs]').forEach(group => {
        const selected = group.querySelector('[data-nl-tab][aria-selected="true"]') || group.querySelector('[data-nl-tab]');
        if (selected) this.activateTab(group, selected.dataset.nlTab, false);
      });
      if (new URL(location.href).searchParams.has('variant') && this.currentVariant) this.showVariantImage(this.currentVariant);
      this.purchaseObserver?.disconnect();
      const purchase = this.querySelector('.nl-purchase-row');
      if (purchase && 'IntersectionObserver' in window) { this.purchaseObserver = new IntersectionObserver(() => this.updateSticky(), { threshold: 0 }); this.purchaseObserver.observe(purchase); }
      this.updateSticky(); this.watchPopup(); this.watchCart();
    }
    setText(selector, text) { this.querySelectorAll(selector).forEach(node => { node.textContent = text || ''; }); }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
    loadImage(container) {
      container?.querySelectorAll('img[data-nl-src]').forEach(img => {
        if (img.dataset.nlSrcset) img.srcset = img.dataset.nlSrcset;
        img.loading = 'eager'; img.src = img.dataset.nlSrc;
        delete img.dataset.nlSrc; delete img.dataset.nlSrcset;
      });
    }
    showSlide(index) {
      if (!this.slides.length) return;
      this.slideIndex = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.slideIndex; if (!slide.hidden) this.loadImage(slide); });
      this.querySelectorAll('[data-nl-thumb]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.nlThumb) === this.slideIndex)));
      this.setText('[data-nl-gallery-count]', String(this.slideIndex + 1));
    }
    activateTab(group, id, focus = false) {
      if (!group) return;
      group.querySelectorAll('[data-nl-tab]').forEach(button => {
        const active = button.dataset.nlTab === id;
        button.setAttribute('aria-selected', String(active)); button.tabIndex = active ? 0 : -1;
        if (active && focus) button.focus();
      });
      group.querySelectorAll('[data-nl-panel]').forEach(panel => { panel.hidden = panel.dataset.nlPanel !== id; if (!panel.hidden) this.loadImage(panel); });
    }
    selectVariant(id, showImage = true, updateUrl = true) {
      const variant = this.variants.find(item => String(item.id) === String(id)) || this.variants[0];
      if (!variant || !this.form) return;
      this.currentVariant = variant;
      this.form.querySelector('[name="id"]').value = variant.id;
      this.querySelectorAll('[data-nl-variant]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.nlVariant === String(variant.id))));
      this.setText('[data-nl-style-name]', variant.title);
      this.setText('[data-nl-price]', variant.priceText);
      this.setText('[data-nl-compare]', variant.compareText);
      this.querySelectorAll('[data-nl-compare],[data-nl-saving]').forEach(node => { node.hidden = !(variant.compare > variant.price); });
      this.setText('[data-nl-saving]', (this.words.saving || 'Save [amount]').replace('[amount]', variant.savingText));
      this.setText('[data-nl-add-label]', variant.available ? this.words.add : this.words.soldout);
      this.querySelectorAll('.nl-add,[data-nl-sticky-add]').forEach(button => { button.disabled = this.busy || !variant.available; });
      const style = [...this.querySelectorAll('[data-nl-style-title]')].find(button => button.dataset.nlStyleTitle.toLowerCase() === variant.title.toLowerCase());
      if (style) this.activateTab(style.closest('[data-nl-tabs]'), style.dataset.nlTab);
      if (showImage) this.showVariantImage(variant);
      if (updateUrl) { const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url); }
    }
    showVariantImage(variant) {
      let index = this.slides.findIndex(slide => slide.dataset.nlVariantName?.toLowerCase() === variant.title.toLowerCase());
      if (index < 0 && variant.imageId) index = this.slides.findIndex(slide => slide.dataset.nlImageId === String(variant.imageId));
      if (index >= 0) this.showSlide(index);
    }
    handleClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const cart = target.closest('[data-nl-cart]');
      if (cart && typeof document.querySelector('cart-drawer')?.open === 'function') { event.preventDefault(); this.openCart(cart); return; }
      const variant = target.closest('[data-nl-variant]');
      if (variant) { this.selectVariant(variant.dataset.nlVariant); return; }
      const thumb = target.closest('[data-nl-thumb]');
      if (thumb) { this.showSlide(Number(thumb.dataset.nlThumb)); return; }
      if (target.closest('[data-nl-prev]')) { this.showSlide(this.slideIndex - 1); return; }
      if (target.closest('[data-nl-next]')) { this.showSlide(this.slideIndex + 1); return; }
      const quantity = target.closest('[data-nl-quantity]');
      if (quantity && this.quantity) { this.quantity.value = Number(this.quantity.value || 1) + Number(quantity.dataset.nlQuantity); this.clampQuantity(); return; }
      const tab = target.closest('[data-nl-tab]');
      if (tab) {
        this.activateTab(tab.closest('[data-nl-tabs]'), tab.dataset.nlTab);
        if (tab.dataset.nlStyleTitle) { const v = this.variants.find(v => v.title.toLowerCase() === tab.dataset.nlStyleTitle.toLowerCase()); if (v) this.selectVariant(v.id); }
        return;
      }
      const zoom = target.closest('[data-nl-zoom-button]');
      if (zoom && performance.now() > (this.noZoomUntil || 0)) {
        const dialog = this.querySelector('[data-nl-lightbox]'), slide = this.slides[this.slideIndex];
        const img = dialog?.querySelector('img');
        if (dialog && img && slide) { this.zoomOpener = zoom; img.src = slide.dataset.nlZoom; img.alt = slide.querySelector('img')?.alt || ''; dialog.showModal(); }
        return;
      }
      if (target.closest('[data-nl-close]') || target.matches('[data-nl-lightbox]')) { this.querySelector('[data-nl-lightbox]')?.close(); return; }
      const anchor = target.closest('a[href^="#nl-"]');
      if (anchor) { const destination = this.querySelector(anchor.getAttribute('href')); if (destination) { event.preventDefault(); destination.scrollIntoView({ behavior: this.motion(), block: 'start' }); } }
    }
    handleKeys(event) {
      const tab = event.target.closest('[data-nl-tab]');
      if (tab && ['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(event.key)) {
        event.preventDefault();
        const group = tab.closest('[data-nl-tabs]'), tabs = [...group.querySelectorAll('[data-nl-tab]')];
        const direction = ['ArrowRight','ArrowDown'].includes(event.key) ? 1 : -1;
        let index = (tabs.indexOf(tab) + direction + tabs.length) % tabs.length;
        if (event.key === 'Home') index = 0; if (event.key === 'End') index = tabs.length - 1;
        const next = tabs[index]; this.activateTab(group, next.dataset.nlTab, true);
        if (next.dataset.nlStyleTitle) { const v = this.variants.find(v => v.title.toLowerCase() === next.dataset.nlStyleTitle.toLowerCase()); if (v) this.selectVariant(v.id); }
      }
      if (event.target.closest('[data-nl-gallery-stage]') && ['ArrowLeft','ArrowRight'].includes(event.key)) { event.preventDefault(); this.showSlide(this.slideIndex + (event.key === 'ArrowRight' ? 1 : -1)); }
    }
    clampQuantity() { if (this.quantity) this.quantity.value = Math.max(1, Math.min(99, Math.floor(Number(this.quantity.value) || 1))); }
    scheduleSticky() { if (this.stickyFrame) return; this.stickyFrame = requestAnimationFrame(() => { this.stickyFrame = null; this.updateSticky(); }); }
    updateSticky() { const bar = this.querySelector('[data-nl-sticky]'), purchase = this.querySelector('.nl-purchase-row'); if (bar) bar.dataset.visible = String(Boolean(purchase && purchase.getBoundingClientRect().bottom < 0)); }
    refreshCartCount() {
      clearTimeout(this.countTimer);
      const version = this.countRequestVersion = (this.countRequestVersion || 0) + 1;
      this.countTimer = setTimeout(() => fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(r => r.json()).then(cart => {
        if (version === this.countRequestVersion && Number.isFinite(cart.item_count)) this.setText('[data-nl-cart-count]', String(cart.item_count));
      }).catch(() => {}), 180);
    }
    watchCart() {
      this.cartObserver?.disconnect();
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      this.cartObserver = new MutationObserver(records => {
        // Native quantity/removal replaces cart markup. Ignore text-only timer ticks.
        const changed = records.some(record => !record.target.closest?.('.cart-timer') && [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1));
        if (changed) this.refreshCartCount();
      });
      this.cartObserver.observe(drawer.closest('.shopify-section') || drawer, { childList: true, subtree: true });
    }
    watchPopup() {
      this.popupObserver?.disconnect();
      const popup = document.querySelector('.vf-root');
      if (!popup) return;
      this.popupWasOpen = popup.classList.contains('open');
      this.popupObserver = new MutationObserver(() => {
        const open = popup.classList.contains('open');
        if (open) document.querySelector('cart-drawer')?.close?.();
        if (this.popupWasOpen && !open) this.refreshCartCount();
        this.popupWasOpen = open;
      });
      this.popupObserver.observe(popup, { attributes: true, attributeFilter: ['class'] });
    }
    async openCart(opener) {
      if (this.openingCart) return;
      this.openingCart = true; opener.setAttribute('aria-busy', 'true');
      try {
        const drawer = document.querySelector('cart-drawer'), sections = drawer.getSectionsToRender().map(s => s.id);
        const response = await fetch(window.routes.cart_update_url + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates: {}, sections, sections_url: location.pathname }) });
        const result = await response.json();
        if (!response.ok || sections.some(id => !result.sections?.[id])) throw new Error('Cart unavailable');
        const parsed = new DOMParser().parseFromString(result.sections['cart-drawer'], 'text/html');
        drawer.classList.toggle('is-empty', parsed.querySelector('cart-drawer')?.classList.contains('is-empty') ?? false);
        drawer.setActiveElement(opener); drawer.renderContents(result); this.refreshCartCount();
      } catch { location.assign(this.dataset.cartUrl); }
      finally { this.openingCart = false; opener.removeAttribute('aria-busy'); }
    }
    async addToCart(event) {
      event.preventDefault();
      if (!this.form || this.busy || !this.currentVariant?.available || !this.form.reportValidity()) return;
      this.busy = true; this.clampQuantity();
      const error = this.querySelector('[data-nl-error]'); if (error) error.hidden = true;
      const opener = event.submitter || this.form.querySelector('[type="submit"]');
      this.querySelectorAll('.nl-add,[data-nl-sticky-add]').forEach(b => { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
      this.setText('[data-nl-add-label]', this.words.adding);
      try {
        const drawer = document.querySelector('cart-drawer');
        const sections = typeof drawer?.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map(s => s.id) : [];
        drawer?.setActiveElement?.(opener);
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: this.currentVariant.id, quantity: Number(this.quantity?.value) || 1 }], sections, sections_url: location.pathname }) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.words.error);
        try {
          if (!sections.length || sections.some(id => !result.sections?.[id])) throw new Error('Cart unavailable');
          drawer.classList.remove('is-empty');
          const popupOpen = Boolean(document.querySelector('.vf-root.open'));
          drawer.renderContents(result, popupOpen); this.refreshCartCount(); this.watchPopup();
        } catch { location.assign(this.dataset.cartUrl); }
      } catch (failure) {
        if (error) { error.textContent = failure.message || this.words.error; error.hidden = false; }
      } finally {
        this.busy = false;
        this.querySelectorAll('.nl-add,[data-nl-sticky-add]').forEach(b => { b.disabled = !this.currentVariant?.available; b.removeAttribute('aria-busy'); });
        this.setText('[data-nl-add-label]', this.currentVariant?.available ? this.words.add : this.words.soldout);
      }
    }
  }
  customElements.define('northlight-page', NorthlightPage);
}

